import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../components/firebase";
import { collection, getDocs, query, where, orderBy, updateDoc, addDoc, deleteDoc } from "firebase/firestore";
import { getDoc, doc } from "firebase/firestore";
import Swal from "sweetalert2";
import AttendanceCalendar from "../../components/AttendanceCalendar";
import { formatWorkHours } from "../../utils/attendanceHelper";

const Attendance = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(""); // Empty = show all records
  const [filterEmail, setFilterEmail] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [holidays, setHolidays] = useState([]);

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate("/");
          return;
        }

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const role = userDocSnap.data().role || "user";
          setUserRole(role);
          
          if (role !== "admin") {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: "Access denied. Admin only.",
              timer: 2000,
            });
            navigate("/dashboard");
            return;
          }
        } else {
          navigate("/dashboard");
          return;
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        navigate("/dashboard");
      }
    };

    checkUserRole();
  }, [navigate]);

  // No need to force today's date - show all records by default

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersList = [];
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          usersList.push({
            id: doc.id,
            email: data.email,
            name: data.username || data.email?.split('@')[0] || "User"
          });
        });
        setUsers(usersList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (userRole === "admin") {
      fetchAttendanceRecords();
      fetchUsers();
      fetchHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, filterDate, filterEmail]);

  const fetchAttendanceRecords = async () => {
    setLoading(true);
    try {
      let q;
      
      // If date filter is provided, filter by date; otherwise get all records
      if (filterDate) {
        // Filter by date - use orderBy on date field to avoid composite index requirement
        q = query(
          collection(db, "attendance"),
          where("date", "==", filterDate),
          orderBy("date", "desc")
        );
      } else {
        // Get all records, ordered by date
        q = query(
          collection(db, "attendance"),
          orderBy("date", "desc")
        );
      }

      const querySnapshot = await getDocs(q);
      const records = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        records.push({ 
          id: doc.id, 
          ...data,
          // Ensure createdAt is available for sorting
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0)
        });
      });

      // Sort by createdAt in memory (most recent first)
      records.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });

      // Filter by name/email if provided
      let filteredRecords = records;
      if (filterEmail) {
        filteredRecords = records.filter((record) =>
          record.userName?.toLowerCase().includes(filterEmail.toLowerCase()) ||
          record.userEmail?.toLowerCase().includes(filterEmail.toLowerCase())
        );
      }

      setAttendanceRecords(filteredRecords);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      // If the error is about missing index, try without orderBy
      if (error.message && error.message.includes("index")) {
        console.log("Index error detected, trying without orderBy...");
        try {
          let q;
          if (filterDate) {
            q = query(
              collection(db, "attendance"),
              where("date", "==", filterDate)
            );
          } else {
            q = query(collection(db, "attendance"));
          }
          
          const querySnapshot = await getDocs(q);
          const records = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            records.push({ 
              id: doc.id, 
              ...data,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0)
            });
          });
          
          // Sort in memory
          records.sort((a, b) => {
            const aTime = a.createdAt?.getTime() || 0;
            const bTime = b.createdAt?.getTime() || 0;
            return bTime - aTime;
          });
          
          let filteredRecords = records;
          if (filterEmail) {
            filteredRecords = records.filter((record) =>
              record.userName?.toLowerCase().includes(filterEmail.toLowerCase()) ||
              record.userEmail?.toLowerCase().includes(filterEmail.toLowerCase())
            );
          }
          
          setAttendanceRecords(filteredRecords);
        } catch (retryError) {
          console.error("Error in retry query:", retryError);
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: "Failed to fetch attendance records",
            text: retryError.message,
            timer: 3000,
          });
        }
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch attendance records",
          text: error.message,
          timer: 3000,
        });
      }
    } finally {
      // Also fetch leaves if we have a filter email (needed for calendar)
      if (filterEmail) {
        try {
          const leavesQ = query(collection(db, "leaves"));
          const leavesSnap = await getDocs(leavesQ);
          const leaves = [];
          leavesSnap.forEach((doc) => {
            const data = doc.data();
            if (data.userEmail?.toLowerCase().includes(filterEmail.toLowerCase()) && data.status === "approved") {
              leaves.push({ id: doc.id, ...data });
            }
          });
          setLeaveRecords(leaves);
        } catch (err) {
          console.error("Error fetching leaves for calendar:", err);
        }
      } else {
        setLeaveRecords([]); // clear leaves if no email selected
      }
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const q = query(collection(db, "holidays"));
      const snapshot = await getDocs(q);
      const fetchedHolidays = [];
      snapshot.forEach(doc => {
        fetchedHolidays.push({ id: doc.id, ...doc.data() });
      });
      setHolidays(fetchedHolidays);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const handleDateClick = async (date, status, holidayName) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (filterEmail) {
      // Employee is selected. Handle completing hours for clicked date.
      const record = attendanceRecords.find(r => r.date === dateStr);

      if (!record) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "No attendance record found for this date.",
          timer: 3000,
        });
        return;
      }

      if (record.totalWorkMinutes >= 510) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "Hours are already 8h 30m or more.",
          timer: 3000,
        });
        return;
      }

      const confirmResult = await Swal.fire({
        title: 'Complete Hours?',
        text: `Are you sure you want to complete hours to 8h 30m for ${dateStr}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, complete it!'
      });

      if (confirmResult.isConfirmed) {
        try {
          const updateData = {
            totalWorkMinutes: 510,
            manuallyAdjustedByAdmin: true
          };
          
          if (!record.logoutTime) {
            updateData.logoutTime = "18:30"; // Set a dummy logout time so it doesn't show as Missing Logout
          }

          await updateDoc(doc(db, "attendance", record.id), updateData);
          
          setAttendanceRecords(prev => prev.map(r => 
            r.id === record.id 
              ? { ...r, ...updateData }
              : r
          ));

          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Hours updated successfully",
            timer: 3000,
          });
        } catch (error) {
          console.error("Error updating hours: ", error);
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: "Failed to update hours",
            timer: 3000,
          });
        }
      }
      return;
    }
    
    // Only allow marking holidays on Global Calendar (when no employee is selected)
    
    if (status === 'HOLIDAY') {
      const holidayDoc = holidays.find(h => h.date === dateStr);
      if (!holidayDoc) return;

      const result = await Swal.fire({
        title: 'Remove Holiday?',
        text: `Do you want to remove "${holidayName}" as a holiday on ${dateStr}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, remove it!'
      });

      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, "holidays", holidayDoc.id));
          Swal.fire('Removed!', 'The holiday has been removed.', 'success');
          fetchHolidays();
        } catch (error) {
          console.error("Error removing holiday:", error);
          Swal.fire('Error', 'Failed to remove holiday', 'error');
        }
      }
    } else {
      const { value: formValues } = await Swal.fire({
        title: 'Add Holiday',
        html:
          `<p>Date: ${dateStr}</p>` +
          '<input id="swal-holiday-name" class="swal2-input" placeholder="Holiday Name (e.g., Diwali)">',
        focusConfirm: false,
        showCancelButton: true,
        preConfirm: () => {
          const name = document.getElementById('swal-holiday-name').value;
          if (!name) {
            Swal.showValidationMessage('Holiday name is required');
          }
          return { name };
        }
      });

      if (formValues) {
        try {
          await addDoc(collection(db, "holidays"), {
            date: dateStr,
            name: formValues.name,
            createdBy: auth.currentUser.email,
            createdAt: new Date().toISOString()
          });
          Swal.fire('Added!', 'Holiday has been saved.', 'success');
          fetchHolidays();
        } catch (error) {
          console.error("Error adding holiday:", error);
          Swal.fire('Error', 'Failed to save holiday', 'error');
        }
      }
    }
  };

  const toggleRowExpansion = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const calculateTotalBreakTime = (breaks) => {
    if (!breaks || breaks.length === 0) return "0 min";
    
    const totalMinutes = breaks.reduce((total, b) => {
      if (b.start && b.end) {
        const start = parseTime(b.start);
        const end = parseTime(b.end);
        const diff = (end - start) / (1000 * 60);
        return total + Math.max(0, diff);
      }
      return total;
    }, 0);

    if (totalMinutes < 60) {
      return `${Math.round(totalMinutes)} min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const formatWorkHours = (minutes) => {
    if (!minutes && minutes !== 0) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getStatus = (record) => {
    if (record.logoutTime) {
      return <span className="badge bg-success">Completed</span>;
    }
    return <span className="badge bg-warning">Open</span>;
  };

  // Handle regularization approval
  const handleApproveRegularization = async (recordId) => {
    try {
      await updateDoc(doc(db, "attendance", recordId), {
        regularization: {
          status: "approved",
          approvedAt: new Date().toISOString(),
          approvedBy: auth.currentUser?.email
        },
        totalWorkMinutes: 510 // Set to 8h 30m
      });

      // Update local state
      setAttendanceRecords(prev => prev.map(r => 
        r.id === recordId 
          ? { ...r, regularization: { ...r.regularization, status: "approved" }, totalWorkMinutes: 510 }
          : r
      ));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization approved",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error approving regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to approve",
        timer: 2000,
      });
    }
  };

  // Handle regularization rejection
  const handleRejectRegularization = async (recordId) => {
    try {
      await updateDoc(doc(db, "attendance", recordId), {
        "regularization.status": "rejected",
        "regularization.rejectedAt": new Date().toISOString(),
        "regularization.rejectedBy": auth.currentUser?.email
      });

      // Update local state
      setAttendanceRecords(prev => prev.map(r => 
        r.id === recordId 
          ? { ...r, regularization: { ...r.regularization, status: "rejected" } }
          : r
      ));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Regularization rejected",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error rejecting regularization:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to reject",
        timer: 2000,
      });
    }
  };

  const handleCompleteTodayHours = async () => {
    if (!filterEmail) return;

    const todayObj = new Date();
    const todayStr = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;
    const todayRecord = attendanceRecords.find(r => r.date === todayStr);

    if (!todayRecord) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "No attendance record found for today.",
        timer: 3000,
      });
      return;
    }

    if (todayRecord.totalWorkMinutes >= 510) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Hours are already 8h 30m or more.",
        timer: 3000,
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: 'Complete Today\'s Hours?',
      text: `Are you sure you want to complete today's hours to 8h 30m for ${filterEmail}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, complete it!'
    });

    if (confirmResult.isConfirmed) {
      try {
        const updateData = {
          totalWorkMinutes: 510,
          manuallyAdjustedByAdmin: true
        };

        if (!todayRecord.logoutTime) {
          updateData.logoutTime = "18:30";
        }

        await updateDoc(doc(db, "attendance", todayRecord.id), updateData);
        
        // Update local state
        setAttendanceRecords(prev => prev.map(r => 
          r.id === todayRecord.id 
            ? { ...r, ...updateData }
            : r
        ));

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Hours updated to 8h 30m successfully",
          timer: 3000,
        });
      } catch (error) {
        console.error("Error updating hours: ", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to update hours",
          timer: 3000,
        });
      }
    }
  };

  if (loading || userRole !== "admin") {
    return (
      <div className="container-fluid p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-2 p-md-4">
      <div className="mb-3 mb-md-4 d-flex justify-content-between align-items-md-center flex-column flex-md-row gap-3">
        <h2 className="fw-bold m-0" style={{ fontSize: isMobile ? "20px" : "24px", color: "#1e293b" }}>Attendance Records</h2>
        
        <div 
          className="d-flex align-items-center bg-light rounded-pill p-1 shadow-sm" 
          style={{ border: "1px solid #e2e8f0", display: "inline-flex" }}
        >
          <button 
            type="button" 
            className={`btn btn-sm rounded-pill d-flex align-items-center justify-content-center transition-all px-3 py-1 m-0 ${viewMode === 'table' ? 'bg-white shadow-sm text-primary fw-bold' : 'text-secondary fw-medium border-0 bg-transparent'}`}
            onClick={() => setViewMode('table')}
            style={{ fontSize: "14px", minWidth: "110px" }}
          >
            <i className="bi bi-table me-2"></i> Table
          </button>
          <button 
            type="button" 
            className={`btn btn-sm rounded-pill d-flex align-items-center justify-content-center transition-all px-3 py-1 m-0 ${viewMode === 'calendar' ? 'bg-white shadow-sm text-primary fw-bold' : 'text-secondary fw-medium border-0 bg-transparent'}`}
            onClick={() => setViewMode('calendar')}
            style={{ fontSize: "14px", minWidth: "120px" }}
          >
            <i className="bi bi-calendar-grid-3x3 me-2"></i> Calendar
          </button>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <div className="calendar-section">
          <div className="mb-4">
            <label className="form-label fw-semibold text-secondary" style={{ fontSize: isMobile ? "13px" : "14px", letterSpacing: "0.3px", textTransform: "uppercase" }}>
              Select Employee
            </label>
            <select
              className="form-select shadow-sm"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              style={{ 
                fontSize: "15px", 
                maxWidth: "400px", 
                borderRadius: "10px", 
                border: "1px solid #cbd5e1",
                padding: "10px 16px",
                color: "#334155"
              }}
            >
              <option value="">-- Choose an Employee --</option>
              {users.map(user => (
                <option key={user.id} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <small className="text-muted d-block mt-2" style={{fontSize: "12px"}}>
              Select an employee from the dropdown to view their detailed attendance and leave calendar.
            </small>
          </div>

          {!filterEmail ? (
            <div>
              <div className="alert alert-info shadow-sm d-flex align-items-center mb-4" role="alert">
                <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                <div>
                  <h6 className="alert-heading mb-1 fw-bold">Global Company Calendar</h6>
                  <p className="mb-0" style={{ fontSize: "14px" }}>Click on any date below to mark it as a holiday or remove an existing holiday. Holidays will be reflected for all users.</p>
                </div>
              </div>
              <AttendanceCalendar 
                attendanceRecords={[]}
                leaveRecords={[]}
                holidays={holidays}
                isMobile={isMobile}
                targetUserName="Global Company"
                onDateClick={handleDateClick}
              />
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-end mb-3">
                <button 
                  className="btn btn-sm btn-success shadow-sm rounded-pill px-3 py-2 fw-semibold d-flex align-items-center"
                  onClick={handleCompleteTodayHours}
                  style={{ fontSize: "14px", letterSpacing: "0.3px", transition: "all 0.2s" }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
                >
                  <i className="bi bi-check-circle-fill me-2" style={{ fontSize: "16px" }}></i> 
                  Complete Today's Hours (8h 30m)
                </button>
              </div>
              <AttendanceCalendar 
                attendanceRecords={attendanceRecords}
                leaveRecords={leaveRecords}
                holidays={holidays}
                isMobile={isMobile}
                onDateClick={handleDateClick}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Date Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-3 mb-md-4 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              const current = new Date(filterDate);
              current.setDate(current.getDate() - 1);
              setFilterDate(current.toISOString().split("T")[0]);
            }}
            style={{ padding: "6px 12px" }}
          >
            ← Prev
          </button>
          <input
            type="date"
            className="form-control"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            style={{ fontSize: isMobile ? "14px" : "16px", width: "auto" }}
          />
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              const current = new Date(filterDate);
              current.setDate(current.getDate() + 1);
              setFilterDate(current.toISOString().split("T")[0]);
            }}
            style={{ padding: "6px 12px" }}
          >
            Next →
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setFilterDate(new Date().toISOString().split("T")[0])}
            style={{ padding: "6px 12px" }}
          >
            Today
          </button>
          {filterDate && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setFilterDate("")}
              style={{ padding: "6px 12px" }}
            >
              Show All
            </button>
          )}
        </div>
        <div style={{ minWidth: isMobile ? "100%" : "220px" }}>
          <select
            className="form-select shadow-sm"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ 
              fontSize: "14px", 
              borderRadius: "8px", 
              border: "1px solid #cbd5e1",
              color: "#334155"
            }}
          >
            <option value="">All Employees</option>
            {users.map(user => (
              <option key={user.id} value={user.email}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div className="row g-3">
          {attendanceRecords.length === 0 ? (
            <div className="col-12">
              <div className="text-center py-4">
                <p className="text-muted">No attendance records found</p>
              </div>
            </div>
          ) : (
            attendanceRecords.map((record) => (
              <div key={record.id} className="col-12">
                <div className="card shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1" style={{ fontSize: "14px", wordBreak: "break-word" }}>
                          {record.userName || record.userEmail?.split("@")[0] || "N/A"}
                        </h6>
                        <small className="text-muted">{formatDate(record.date)}</small>
                      </div>
                      <div>{getStatus(record)}</div>
                    </div>
                    
                    <hr className="my-2" />
                    
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Login</small>
                        <strong style={{ fontSize: "13px" }}>{formatTime(record.loginTime)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Logout</small>
                        <strong style={{ fontSize: "13px" }}>{formatTime(record.logoutTime)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Break Time</small>
                        <strong style={{ fontSize: "13px" }}>{calculateTotalBreakTime(record.breaks)}</strong>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">Working Hours</small>
                        <strong style={{ fontSize: "13px" }}>{formatWorkHours(record.totalWorkMinutes)}</strong>
                      </div>
                    </div>

                    {/* Regularization Section for Mobile */}
                    {record.regularization?.status === "pending" && (
                      <div className="mt-2 p-2 bg-warning bg-opacity-25 rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="fw-bold">Regularization Request</small>
                            <p className="mb-0" style={{ fontSize: "11px" }}>{record.regularization.reason}</p>
                          </div>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleApproveRegularization(record.id)}
                            >
                              ✓
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRejectRegularization(record.id)}
                            >
                              ✗
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {record.regularization?.status === "approved" && (
                      <div className="mt-2">
                        <span className="badge bg-success">Regularised</span>
                      </div>
                    )}
                    {record.regularization?.status === "rejected" && (
                      <div className="mt-2">
                        <span className="badge bg-danger">Regularization Rejected</span>
                      </div>
                    )}

                    {record.breaks && record.breaks.length > 0 && (
                      <div className="mt-2">
                        <button
                          className="btn btn-sm btn-outline-primary w-100"
                          onClick={() => toggleRowExpansion(record.id)}
                          style={{ fontSize: "12px" }}
                        >
                          {expandedRows.has(record.id) ? "Hide" : "Show"} Breaks ({record.breaks.length})
                        </button>
                        {expandedRows.has(record.id) && (
                          <div className="mt-2 p-2 bg-light rounded">
                            <h6 className="fw-bold mb-2" style={{ fontSize: "13px" }}>Break Details:</h6>
                            {record.breaks.map((b, index) => (
                              <div key={index} className="mb-1" style={{ fontSize: "12px" }}>
                                <span className="badge bg-info me-2">
                                  {b.type || "Break"} {index + 1}
                                </span>
                                <span>
                                  {formatTime(b.start)} - {formatTime(b.end) || "Active"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>User Name</th>
                <th>Login Time</th>
                <th>Logout Time</th>
                <th>Break Time</th>
                <th>Total Working Hours</th>
                <th>Date</th>
                <th>Status</th>
                <th>Regularization</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {attendanceRecords.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                attendanceRecords.map((record) => (
                  <React.Fragment key={record.id}>
                    <tr>
                      <td style={{ wordBreak: "break-word", maxWidth: "200px" }}>{record.userName || record.userEmail?.split("@")[0] || "N/A"}</td>
                      <td>{formatTime(record.loginTime)}</td>
                      <td>{formatTime(record.logoutTime)}</td>
                      <td>{calculateTotalBreakTime(record.breaks)}</td>
                      <td>{formatWorkHours(record.totalWorkMinutes)}</td>
                      <td>{formatDate(record.date)}</td>
                      <td>{getStatus(record)}</td>
                      <td>
                        {record.regularization?.status === "pending" ? (
                          <div>
                            <div className="mb-1" style={{ fontSize: "11px", color: "#64748b", maxWidth: "150px" }} title={record.regularization.reason}>
                              {record.regularization.reason?.length > 30 
                                ? record.regularization.reason.substring(0, 30) + "..." 
                                : record.regularization.reason}
                            </div>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApproveRegularization(record.id)}
                                title="Approve"
                              >
                                ✓ Approve
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRejectRegularization(record.id)}
                                title="Reject"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        ) : record.regularization?.status === "approved" ? (
                          <span className="badge bg-success">Regularised</span>
                        ) : record.regularization?.status === "rejected" ? (
                          <span className="badge bg-danger">Rejected</span>
                        ) : record.totalWorkMinutes < 510 && record.logoutTime ? (
                          <span className="badge bg-secondary">Not Requested</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {record.breaks && record.breaks.length > 0 && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => toggleRowExpansion(record.id)}
                          >
                            {expandedRows.has(record.id) ? "Hide" : "Show"} Breaks
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedRows.has(record.id) && record.breaks && record.breaks.length > 0 && (
                      <tr>
                        <td colSpan="9">
                          <div className="p-3 bg-light rounded">
                            <h6 className="fw-bold mb-2">Break Details:</h6>
                            {record.breaks.map((b, index) => (
                              <div key={index} className="mb-2">
                                <span className="badge bg-info me-2">
                                  {b.type || "Break"} {index + 1}
                                </span>
                                <span>
                                  {formatTime(b.start)} - {formatTime(b.end) || "Active"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Attendance;

